import { useMemo } from 'react';
import { Typography, Card, Row, Col, Spin, Tooltip, Collapse } from 'antd';
import { BarChartOutlined, ArrowRightOutlined, FileSearchOutlined, CheckCircleOutlined, SyncOutlined, MinusCircleOutlined, CarOutlined, CloseCircleOutlined, StopOutlined, EditOutlined, CheckCircleFilled, ClockCircleOutlined, ExclamationCircleOutlined, TableOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import './order-analysis.css';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const OrderAnalysis = ({ servicesData, loading, currentDateInfo }) => {
  const navigate = useNavigate();

  const handleGoToOrders = () => {
    navigate('/orders/all/1');
  };

  const statusConfig = {
    1: { icon: <FileSearchOutlined />, color: 'purple', text: 'En revisión' },
    2: { icon: <CheckCircleOutlined />, color: 'gold', text: 'En cotización' },
    3: { icon: <CheckCircleOutlined />, color: 'default', text: 'Pendiente de aprobación' },
    4: { icon: <SyncOutlined />, color: 'processing', text: 'En construcción' },
    5: { icon: <MinusCircleOutlined />, color: 'cyan', text: 'Inspección' },
    6: { icon: <CarOutlined />, color: 'geekblue', text: 'En camino' },
    7: { icon: <CheckCircleOutlined />, color: 'success', text: 'Entregado' },
    8: { icon: <CloseCircleOutlined />, color: 'error', text: 'Rechazado' },
    9: { icon: <CloseCircleOutlined />, color: 'magenta', text: 'Cancelado' },
    10: { icon: <CloseCircleOutlined />, color: '#72132c', text: 'En corrección' },
    11: { icon: <CloseCircleOutlined />, color: '#F5222D', text: 'Cancelación solicitada' },
    12: { icon: <CloseCircleOutlined />, color: '#F5222D', text: 'En queja' }
  };

  const metrics = useMemo(() => {
    if (!servicesData) {
      return {
        totalServices: 0,
        statusCounts: {},
        statusDetails: []
      };
    }

    const totalServices = (servicesData.services_by_category || []).reduce(
      (sum, cat) => sum + (cat.count || 0), 0
    );

    const servicesByStatus = servicesData.services_by_status || [];
    const statusCounts = {};
    const statusDetails = [];

    servicesByStatus.forEach(status => {
      const statusCode = status.status_code;
      const statusInfo = statusConfig[statusCode];
      if (statusInfo) {
        statusCounts[statusCode] = {
          count: status.count || 0,
          name: status.status_name || statusInfo.text,
          color: statusInfo.color,
          icon: statusInfo.icon,
          code: statusCode
        };

        statusDetails.push({
          code: statusCode,
          name: status.status_name || statusInfo.text,
          count: status.count || 0,
          color: statusInfo.color,
          icon: statusInfo.icon
        });
      }
    });

    return {
      totalServices,
      statusCounts,
      statusDetails: statusDetails.sort((a, b) => a.code - b.code)
    };
  }, [servicesData]);

  const orderMetrics = useMemo(() => {
    return [
      {
        title: 'En revisión',
        value: metrics.statusCounts[1]?.count || 0,
        icon: <FileSearchOutlined />,
        color: 'purple',
        key: 'review',
        statusCode: 1
      },
      {
        title: 'En cotización',
        value: metrics.statusCounts[2]?.count || 0,
        icon: <CheckCircleOutlined />,
        color: 'gold',
        key: 'quotation',
        statusCode: 2
      },
      {
        title: 'Pendiente aprobación',
        value: metrics.statusCounts[3]?.count || 0,
        icon: <CheckCircleOutlined />,
        color: 'default',
        key: 'pending_approval',
        statusCode: 3
      },
      {
        title: 'En construcción',
        value: metrics.statusCounts[4]?.count || 0,
        icon: <SyncOutlined spin />,
        color: 'processing',
        key: 'construction',
        statusCode: 4
      },
      {
        title: 'Inspección',
        value: metrics.statusCounts[5]?.count || 0,
        icon: <MinusCircleOutlined />,
        color: 'cyan',
        key: 'inspection',
        statusCode: 5
      },
      {
        title: 'En camino',
        value: metrics.statusCounts[6]?.count || 0,
        icon: <CarOutlined />,
        color: 'geekblue',
        key: 'onWay',
        statusCode: 6
      },
      {
        title: 'Entregadas',
        value: metrics.statusCounts[7]?.count || 0,
        icon: <CheckCircleFilled />,
        color: 'success',
        key: 'delivered',
        statusCode: 7
      },
      {
        title: 'Rechazadas',
        value: metrics.statusCounts[8]?.count || 0,
        icon: <CloseCircleOutlined />,
        color: 'error',
        key: 'rejected',
        statusCode: 8
      },
      {
        title: 'Canceladas',
        value: metrics.statusCounts[9]?.count || 0,
        icon: <StopOutlined />,
        color: 'magenta',
        key: 'cancelled',
        statusCode: 9
      },
      {
        title: 'En corrección',
        value: metrics.statusCounts[10]?.count || 0,
        icon: <EditOutlined />,
        color: '#72132c',
        key: 'correction',
        statusCode: 10
      },
      {
        title: 'Cancelación solicitada',
        value: metrics.statusCounts[11]?.count || 0,
        icon: <ClockCircleOutlined />,
        color: '#F5222D',
        key: 'cancellation_requested',
        statusCode: 11
      },
      {
        title: 'En queja',
        value: metrics.statusCounts[12]?.count || 0,
        icon: <ExclamationCircleOutlined />,
        color: '#F5222D',
        key: 'complaint',
        statusCode: 12
      }
    ];
  }, [metrics]);

  const pieChartData = useMemo(() => {
    const totalDelivered = metrics.statusCounts[7]?.count || 0;
    const totalInProgress = (metrics.statusCounts[4]?.count || 0) +
      (metrics.statusCounts[5]?.count || 0) +
      (metrics.statusCounts[6]?.count || 0);
    const totalPending = (metrics.statusCounts[1]?.count || 0) +
      (metrics.statusCounts[2]?.count || 0) +
      (metrics.statusCounts[3]?.count || 0);
    const totalProblems = (metrics.statusCounts[8]?.count || 0) +
      (metrics.statusCounts[9]?.count || 0) +
      (metrics.statusCounts[10]?.count || 0) +
      (metrics.statusCounts[11]?.count || 0) +
      (metrics.statusCounts[12]?.count || 0);

    return [
      { name: 'Entregadas', value: totalDelivered, color: '#52c41a' },
      { name: 'En progreso', value: totalInProgress, color: '#1890ff' },
      { name: 'Pendientes', value: totalPending, color: '#faad14' },
      { name: 'Con incidencias', value: totalProblems, color: '#ff4d4f' },
    ];
  }, [metrics.statusCounts]);

  const COLORS = ['#52c41a', '#1890ff', '#faad14', '#ff4d4f'];

  if (loading) {
    return (
      <Card className="order-analysis-card">
        <div className="card-header">
          <div className="card-icon card-icon-order-analysis">
            <BarChartOutlined />
          </div>
          <div>
            <Title level={3}>Análisis de Órdenes</Title>
            <Text type="secondary">Estado de servicios por categoría</Text>
          </div>
        </div>
        <div className="card-content">
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((_, index) => (
              <Col xs={12} sm={8} md={6} lg={4} xl={3} key={index}>
                <div className="order-metric-skeleton">
                  <Spin size="small" />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Card>
    );
  }

  return (
    <Card className="order-analysis-card">
      <div className="card-header">
        <Tooltip
          title="Click aquí para ir a Órdenes Generales. Ver el detalle completo de todas las órdenes."
          placement="top"
          mouseEnterDelay={0.3}
        >
          <div
            className="card-icon card-icon-order-analysis clickable-icon"
            onClick={handleGoToOrders}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <BarChartOutlined />
            <ArrowRightOutlined className="icon-hover-arrow" />
          </div>
        </Tooltip>
        <div>
          <Tooltip
            title="Click aquí para ir a Órdenes Generales. Ver el detalle completo de todas las órdenes."
            placement="top"
            mouseEnterDelay={0.3}
          >
            <Title
              level={3}
              style={{ cursor: 'pointer' }}
              onClick={handleGoToOrders}
              className="clickable-title"
            >
              Análisis de Órdenes
            </Title>
          </Tooltip>
          <Text type="secondary">{currentDateInfo || `Estado de servicios por categoría - Total: ${metrics.totalServices} servicios`}</Text>
        </div>
      </div>
      <div className="card-content">
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            {orderMetrics.map((metric, index) => (
              <Col xs={12} sm={8} md={6} lg={4} xl={3} key={metric.key}>
                <div className="order-metric">
                  <div className={`order-metric-icon order-metric-${metric.color}`} style={{ position: 'relative' }}>
                    {metric.icon}
                  </div>
                  <div className="order-metric-content">
                    <div className="order-metric-value">{metric.value}</div>
                    <div className="order-metric-title">{metric.title}</div>
                    {metric.statusCode && (
                      <div className="order-metric-percentage">
                        {metrics.totalServices > 0
                          ? `${Math.round((metric.value / metrics.totalServices) * 100)}%`
                          : '0%'}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        <div style={{ marginTop: 24 }}>
          <Title level={5}>Distribución general de órdenes</Title>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => [value, 'Cantidad']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
};

export default OrderAnalysis;